import { useEffect } from 'react'

import {
  getDateRangeFromUrlQuery,
  resolvePageContextFromUrl,
} from '../pageContext'
import { RENDERING_RULES, SEGMENT_RULES, TIME_TRACKING_RULES } from '../prompts'
import { scrivitoDataAccessAdapter } from './scrivitoDataAccessAdapter'
import {
  createScrivitoHostBindings,
  mapFlagsToCapabilities,
} from './scrivitoHostBindings'
import { scrivitoNavigationConnector } from './scrivitoNavigationConnector'
import { setHostRuntime, setHostToolPack } from '../../core/engine/host-runtime'
import { useAiChatBotStore } from '../../core/store/useAiChatBotStore'
import { ENTITY_REGISTRY_METADATA } from '../registry'
import { AGGREGATION_TOOLS, FILTER_TOOLS } from '../tools/registry'
import { DataClassName } from '@/Data/const'
import { useFeatureFlags } from '@/hooks/useFeatureFlagHandler'

/**
 * Returns true when the bot should be shown (crmBot capability + optional extra condition).
 * Side-effect: syncs the capability set into the bot store and host runtime so open() and
 * tool execution can gate behaviour (e.g. filter tools behind advancedSearch) without
 * needing React context access.
 *
 * This hook is the Scrivito host boundary — it is the only place in the AiChatBot tree
 * that reads CRM feature flags from React context, so it lives under host/scrivito/ rather
 * than in the host-neutral core. All CRM→capability mapping is delegated to
 * host/scrivito/scrivitoHostBindings.ts.
 */
export function useAiChatBotEnabled(extra: boolean = true): boolean {
  const { flags } = useFeatureFlags()
  const { setFeatureFlags } = useAiChatBotStore.getState()

  useEffect(() => {
    if (!flags) return
    const capabilities = mapFlagsToCapabilities(flags)
    setFeatureFlags(capabilities)
    setHostRuntime({
      data: scrivitoDataAccessAdapter,
      navigation: scrivitoNavigationConnector,
      bindings: createScrivitoHostBindings(capabilities),
      registry: ENTITY_REGISTRY_METADATA,
      resolvePageFromUrl: resolvePageContextFromUrl,
      getUrlDateRange: getDateRangeFromUrlQuery,
    })
    // Resolve whether the List (Segment) entity is enabled on this instance so
    // we can conditionally provide the segment-instructions prompt fragment.
    // Mirrors the isEntityEnabled logic without calling it (to avoid depending on
    // the host runtime being readable during this same effect).
    const listMeta = ENTITY_REGISTRY_METADATA[DataClassName.LIST]
    const listEnabled =
      !listMeta?.requiredFlag || capabilities.has(listMeta.requiredFlag)
    setHostToolPack({
      aggregationTools: AGGREGATION_TOOLS,
      filterTools: FILTER_TOOLS,
      promptFragments: {
        renderingRules: RENDERING_RULES,
        timeTrackingRules: TIME_TRACKING_RULES,
        segmentInstructions: listEnabled ? SEGMENT_RULES : undefined,
      },
      entityDisplayNames: { [DataClassName.LIST]: 'Segment' },
    })
  }, [flags, setFeatureFlags])

  return mapFlagsToCapabilities(flags ?? []).has('crmBot') && extra
}
